/**
 * Equipments slice - single source for merchant equipment API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { Equipments, STATUSES } from '../../api/endpoints';

export const getMerchantEquipment = createAsyncThunk(
  'equipment/getMerchantEquipment',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(Equipments.GETEQUIPMENTDBA(), { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch merchant equipments');
    }
  }
);

/**
 * GET /statuses?model=equipments - fetch status options for equipment
 */
export const getEquipmentStatuses = createAsyncThunk(
  'equipment/getEquipmentStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(STATUSES.LIST, { params: { 'conditions[model]': 'Equipment' } });
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      return Array.isArray(list) ? list : [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch equipment statuses');
    }
  }
);

/**
 * POST /equipments - create new equipment.
 * Body: dba_id, equipment_type_id, equipment_model_id, manufacturer_id, status_id, sku_number, add_terminal, add_gateway, note, newfield.
 * newfield: { "equipment-type": "", "manufacturer": "", "equipment-model": "" } when user types new values instead of selecting.
 */
export const createEquipment = createAsyncThunk(
  'equipment/createEquipment',
  async (payload, { rejectWithValue }) => {
    try {
      const body = {
        dba_id: payload.dba_id,
        equipment_type_id: payload.equipment_type_id || '',
        equipment_model_id: payload.equipment_model_id || '',
        manufacturer_id: payload.manufacturer_id || '',
        status_id: payload.status_id,
        sku_number: payload.sku_number || '',
        add_terminal: payload.add_terminal ?? '',
        add_gateway: payload.add_gateway ?? '',
        note: payload.note || '',
        new_fields: {
          'equipment_type': payload.equipment_type_custom ?? '',
          manufacturer: payload.manufacturer_custom ?? '',
          'equipment_model': payload.equipment_model_custom ?? '',
        },
      };
      const { data } = await api.post(Equipments.LIST, body);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add equipment');
    }
  }
);

/**
 * GET /equipments/:id/equipment-logs - fetch equipment log history
 */
export const getEquipmentLogs = createAsyncThunk(
  'equipment/getEquipmentLogs',
  async (equipmentId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(Equipments.EQUIPMENT_LOGS(equipmentId));
      const logs = data?.equipment_logs ?? data?.data?.equipment_logs ?? [];
      return Array.isArray(logs) ? logs : [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch equipment logs');
    }
  }
);

/**
 * DELETE /equipments/:id - delete equipment
 */
export const deleteEquipment = createAsyncThunk(
  'equipment/deleteEquipment',
  async (equipmentId, { rejectWithValue }) => {
    try {
      await api.delete(Equipments.BY_ID(equipmentId));
      return equipmentId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete equipment');
    }
  }
);

/**
 * PUT /equipments/:id/status - update equipment status only.
 * Body: _method, status_id, note, sku_number.
 */
export const updateEquipmentStatus = createAsyncThunk(
  'equipment/updateEquipmentStatus',
  async ({ equipmentId, payload }, { rejectWithValue }) => {
    try {
      const body = {
        _method: 'PUT',
        status_id: payload.status_id || '',
        note: payload.note || '',
        sku_number: payload.sku_number || '',
      };
      const { data } = await api.put(Equipments.STATUS(equipmentId), body);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update equipment status');
    }
  }
);

const initialState = {
  merchantEquipments: null,
  statusOptions: [],
  equipmentLogs: [],
  logsLoading: false,
  loading: false,
  updateLoading: false,
  createLoading: false,
  error: null,
};

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    clearMerchantEquipments: (state) => {
      state.merchantEquipments = null;
    },
    clearEquipmentLogs: (state) => {
      state.equipmentLogs = [];
    },
    clearEquipmentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMerchantEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMerchantEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.merchantEquipments = action.payload;
      })
      .addCase(getMerchantEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      })
      .addCase(getEquipmentStatuses.fulfilled, (state, action) => {
        state.statusOptions = action.payload ?? [];
      })
      .addCase(createEquipment.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createEquipment.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createEquipment.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || 'Failed to add equipment';
      })
      .addCase(getEquipmentLogs.pending, (state) => {
        state.logsLoading = true;
        state.equipmentLogs = [];
      })
      .addCase(getEquipmentLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.equipmentLogs = action.payload ?? [];
      })
      .addCase(getEquipmentLogs.rejected, (state) => {
        state.logsLoading = false;
        state.equipmentLogs = [];
      })
      .addCase(updateEquipmentStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateEquipmentStatus.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateEquipmentStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || 'Failed to update equipment status';
      })
      .addCase(deleteEquipment.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete equipment';
      });
  },
});

export const { clearMerchantEquipments, clearEquipmentLogs, clearEquipmentError } = equipmentSlice.actions;
export default equipmentSlice.reducer;
